<script setup>
import {
    ChatBubbleLeftRightIcon,
    CheckBadgeIcon,
    CloudArrowDownIcon,
    DocumentTextIcon,
    LightBulbIcon,
    MicrophoneIcon,
} from "@heroicons/vue/20/solid";
import { Link } from "@inertiajs/vue3";

import InfoLayout from "@/Layouts/InfoLayout.vue";

import BentoSection from "@/Pages/Info/Partials/BentoSection.vue";
import ConnectSection from "@/Pages/Info/Partials/ConnectSection.vue";
import DemoSection from "@/Pages/Info/Partials/DemoSection.vue";
import FaqSection from "@/Pages/Info/Partials/FaqSection.vue";
import FeatureSection from "@/Pages/Info/Partials/FeatureSection.vue";
import ImageSection from "@/Pages/Info/Partials/ImageSection.vue";
import LargeTestimonialFigure from "@/Pages/Info/Partials/LargeTestimonialFigure.vue";
import LearnSection from "@/Pages/Info/Partials/LearnSection.vue";
import PricingCards from "@/Pages/Info/Partials/PricingCards.vue";
import ProcessAnimation from "@/Pages/Info/Partials/ProcessAnimation.vue";
import ScreenshotSection from "@/Pages/Info/Partials/ScreenshotSection.vue";
import ScreenshotSectionParagraph from "@/Pages/Info/Partials/ScreenshotSectionParagraph.vue";
import TestimonialFigure from "@/Pages/Info/Partials/TestimonialFigure.vue";

import HeadingThree from "@/Components/HeadingThree.vue";
import LinkInline from "@/Components/LinkInline.vue";

defineProps({
    acceptDemo: Boolean,
    demoUrl: String,
    tiers: Array,
});

const features = [
    {
        name: "Engaging interviews.",
        description:
            "Surveys are boring. People want to chat. Interviews encourage your customers to give their feedback.",
        icon: ChatBubbleLeftRightIcon,
    },
    {
        name: "Qualta Summary.",
        description:
            "Get the main themes and insights from the interviews that you can put into action.",
        icon: LightBulbIcon,
    },
    {
        name: "Prompt templates.",
        description:
            "Set up your first interview fast using our Guide prompt templates or AI assistance.",
        icon: DocumentTextIcon,
    },
    {
        name: "Unlimited exports.",
        description:
            "Export single interviews or all at once in CSV, Word, and PDF format.",
        icon: CloudArrowDownIcon,
    },
    {
        name: "Speech first.",
        description:
            "Respondents are encourage to speak to Qualta with lengthy, in-depth replies.",
        icon: MicrophoneIcon,
    },
    {
        name: "Custom branding.",
        description:
            "Brand the interview chat screen with your assets on Pro and Business plans.",
        icon: CheckBadgeIcon,
    },
];

const learnings = [
    {
        name: "Guide Testing",
        description:
            "Fully test and adjust your interview Guide before collecting responses. Be confident that you will get the feedback you need.",
        icon: "fass fa-vial-circle-check",
        url: "https://support.qualta.io/article/45-how-to-test-a-guide",
    },
    {
        name: "Search Interviews",
        description:
            "Perform keyword searches to find specific language and common terms across multiple interviews.",
        icon: "fass fa-magnifying-glass",
        url: "https://support.qualta.io/article/46-how-to-search-guide-interviews",
    },
    {
        name: "Ask Qualta",
        description:
            "Analyze interview results by asking Qualta AI your most probing questions and get actionable answers (on Pro and Business plans).",
        icon: "fass fa-brain-circuit",
        url: "https://support.qualta.io/article/47-how-to-analyze-a-guide-ask-vamp",
    },
];
</script>

<template>
    <InfoLayout
        canonical-link="https://qualta.io/"
        :force-dark="true"
        title="Home"
    >
        <!-- Hero section -->
        <div
            class="relative isolate max-sm:min-h-[100dvh] overflow-hidden bg-gray-900 pb-16 pt-14 sm:pb-20"
        >
            <img
                src="/assets/hero_splash.webp"
                alt="business woman conducting customer interview"
                class="absolute inset-0 -z-10 size-full aspect-[1155/678] object-cover"
                loading="eager"
                fetchpriority="high"
            />
            <div
                class="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
                aria-hidden="true"
            >
                <div
                    class="relative left-[calc(50%-11rem)] aspect-1155/678 w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-linear-to-tr from-[#ff80b5] to-[#9089fc] opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
                    style="
                        clip-path: polygon(
                            74.1% 44.1%,
                            100% 61.6%,
                            97.5% 26.9%,
                            85.5% 0.1%,
                            80.7% 2%,
                            72.5% 32.5%,
                            60.2% 62.4%,
                            52.4% 68.1%,
                            47.5% 58.3%,
                            45.2% 34.5%,
                            27.5% 76.7%,
                            0.1% 64.9%,
                            17.9% 100%,
                            27.6% 76.8%,
                            76.1% 97.7%,
                            74.1% 44.1%
                        );
                    "
                />
            </div>
            <div
                class="mx-auto max-w-(--breakpoint-2xl) px-6 lg:px-8 lg:grid lg:grid-cols-2 lg:gap-x-16"
            >
                <div class="mx-auto max-w-2xl py-24 lg:py-56">
                    <div
                        class="h-full flex flex-col justify-between gap-y-16 text-center lg:text-left"
                    >
                        <h1
                            class="text-balance text-5xl font-semibold tracking-tight text-white sm:text-7xl"
                        >
                            AI-driven<br />
                            research<br class="max-lg:hidden" />
                            interviews
                        </h1>
                        <div
                            class="mx-auto w-full max-w-lg aspect-video overflow-hidden rounded-[1rem] lg:hidden has-[:fullscreen]:block!"
                        >
                            <ProcessAnimation />
                        </div>
                        <div
                            class="lg:pb-4 flex items-center justify-center lg:justify-start gap-x-8"
                        >
                            <Link
                                :href="route('register')"
                                class="rounded-md bg-brand-dark px-3.5 py-2.5 text-sm font-semibold text-white uppercase shadow-xs hover:bg-brand-dark-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-light"
                                prefetch
                                >Start For FREE</Link
                            >
                            <div class="flex gap-x-10">
                                <a
                                    href="#feature-section"
                                    class="text-white font-medium flex items-center gap-2"
                                    ><span>Learn More</span>
                                    <faIcon
                                        :icon="['fasr', 'fa-angles-down']"
                                        aria-hidden="true"
                                /></a>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    class="mx-auto lg:my-56 w-full max-w-2xl aspect-video hidden lg:block rounded-[1rem] overflow-hidden has-[:fullscreen]:block!"
                >
                    <ProcessAnimation />
                </div>
            </div>
            <div
                class="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
                aria-hidden="true"
            >
                <div
                    class="relative left-[calc(50%+3rem)] aspect-1155/678 w-[36.125rem] -translate-x-1/2 bg-linear-to-tr from-[#ff80b5] to-[#9089fc] opacity-20 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
                    style="
                        clip-path: polygon(
                            74.1% 44.1%,
                            100% 61.6%,
                            97.5% 26.9%,
                            85.5% 0.1%,
                            80.7% 2%,
                            72.5% 32.5%,
                            60.2% 62.4%,
                            52.4% 68.1%,
                            47.5% 58.3%,
                            45.2% 34.5%,
                            27.5% 76.7%,
                            0.1% 64.9%,
                            17.9% 100%,
                            27.6% 76.8%,
                            76.1% 97.7%,
                            74.1% 44.1%
                        );
                    "
                />
            </div>
        </div>

        <!-- Feature section -->
        <FeatureSection :features="features">
            <template #subtitle> Everything you need </template>
            <template #title> Interviews with real insights </template>
            <template #description>
                Every plan has convenience built in with an AI interviewer,
                Qualta summaries, and unlimited data exports.
            </template>
        </FeatureSection>

        <!-- Why Interviews section -->
        <ScreenshotSection>
            <template #subtitle> Data with context </template>
            <template #title> Why interviews? </template>
            <template #content>
                <ScreenshotSectionParagraph>
                    Surveys are often too narrow in scope and provide
                    questionable results. Interviews offer data that is hard to
                    deny, because the interviewer can ask clarifying questions
                    and broaden the conversation as needed to mine real
                    information. Interviews engage the respondent to elicit
                    honest and deep feedback.
                </ScreenshotSectionParagraph>
                <ScreenshotSectionParagraph>
                    With minimal prompting, Qualta AI can conduct an interview
                    about virtually anything.
                </ScreenshotSectionParagraph>
                <TestimonialFigure
                    image="/assets/jake_tuber_profile.webp"
                    image-alt="Jake Tuber profile picture"
                    name="Dr. Jake Tuber"
                    title="Principal at Ticonderoga Advisory"
                >
                    <p>
                        “Interviews have always been the gold standard – but
                        they've been expensive. Qualta AI gives you interview
                        depth at less than the cost of a survey. The best of
                        both worlds is finally here.”
                    </p>
                </TestimonialFigure>
            </template>
        </ScreenshotSection>

        <!-- Learning section -->
        <LearnSection :learnings="learnings">
            <template #subtitle> Learn faster </template>
            <template #title> Get the answers you're looking for </template>
            <template #description>
                Qualta has integrated tools to help you create the perfect
                interview experience and get the information you need, when you
                need it.
            </template>
        </LearnSection>

        <!-- Connect Section -->
        <ConnectSection>
            <template #subtitle> Stay productive </template>
            <template #title>
                Connect Qualta with your favorite tools
            </template>
            <template #content>
                <p class="mt-6 text-lg/8 text-gray-600 dark:text-gray-300">
                    Send interview data wherever your team works. Plug Qualta
                    into your CRM, automation platform, or AI agent in minutes.
                </p>
                <HeadingThree> API </HeadingThree>
                <p class="my-2">
                    Take control of your interview data with our simple, robust
                    API. Backup transcripts, analyze results, or transport data
                    to a third-party tool with our
                    <LinkInline as="a" href="/docs/api"
                        >detailed documentation</LinkInline
                    >.
                </p>
                <HeadingThree> Webhooks </HeadingThree>
                <p class="my-2">
                    Get notified the moment an interview is completed or a guide
                    changes. Point a webhook at Zapier, n8n, or your own
                    endpoint to automate the next step.
                </p>
                <HeadingThree> MCP Server </HeadingThree>
                <p class="my-2">
                    Connect Qualta to the AI agent of your choice with our
                    <LinkInline :href="route('info.apps.mcp-server')"
                        >Model Context Protocol (MCP) server</LinkInline
                    >. Your agent can securely search your guides and read
                    interview transcripts as context.
                </p>
            </template>
        </ConnectSection>

        <!-- Testimonial section -->
        <LargeTestimonialFigure
            class="mt-40 sm:mt-64"
            image="/assets/bryan_speece_profile_4.webp"
            image-alt="Bryan Speece profile picture"
            name="Bryan Speece"
            title="CEO, Qualta AI"
        >
            <p>
                Conducting one-on-one customer interviews is the ideal method
                for driving product development and business growth. Qualta
                unlocks this superpower at scale.
            </p>
        </LargeTestimonialFigure>

        <!-- Speech-first Interviews -->
        <ImageSection>
            <template #subtitle> Speech-first interviews </template>
            <template #title>
                The AI agent built for Qualitative Research
            </template>
            <template #content>
                <ScreenshotSectionParagraph>
                    Qualta AI is an agent fine-tuned to conduct insightful
                    interviews and collect holistic qualitative data. That data
                    provides rich and complex perspectives, nested in real
                    context. We help you make sense of it all with analytical
                    features like the Qualta Summary and Ask Qualta.
                </ScreenshotSectionParagraph>
                <ScreenshotSectionParagraph>
                    Respondents can type or talk, but are encourage to speak
                    with lengthy, in-depth replies. You may select the voice
                    audio used when conducting the interview. With multiple
                    voices available, you can choose the one that reflects your
                    brand.
                </ScreenshotSectionParagraph>
            </template>
            <template #image>
                <img
                    src="/assets/01KT70WEAC59X103HZHE3E579G.webp"
                    alt=""
                    height="903"
                    width="508"
                    class="-mb-12 -mt-30 ml-6 sm:ml-12 md:ml-28 lg:ml-4 xl:ml-6"
                />
            </template>
        </ImageSection>

        <!-- Create, Share, Review section -->
        <BentoSection />

        <!-- Pricing section -->
        <PricingCards :tiers="tiers" />

        <!-- Demo section -->
        <DemoSection :accept-demo="acceptDemo" :demo-url="demoUrl" />

        <!-- FAQ section -->
        <FaqSection />
    </InfoLayout>
</template>
